import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Notification } from '../api/notifications';
import { Task } from '../api/tasks';
import { getApiHostUrl } from '../config/api';

function getSocketBaseUrl(): string {
  return getApiHostUrl();
}

interface UseWebSocketOptions {
  userId?: string;
  modules?: string[];
  autoConnect?: boolean;
  onNotificationReceived?: (notification: Notification) => void;
  onNotificationDeleted?: (notificationId: string) => void;
  onTaskReceived?: (task: Task) => void;
  onUrgentNotification?: (notification: Notification) => void;
}

interface WebSocketState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  lastMessage: unknown;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const {
    userId,
    modules = [],
    autoConnect = true,
    onNotificationReceived,
    onNotificationDeleted,
    onTaskReceived,
    onUrgentNotification,
  } = options;

  const onNotificationReceivedRef = useRef(onNotificationReceived);
  const onNotificationDeletedRef = useRef(onNotificationDeleted);
  const onTaskReceivedRef = useRef(onTaskReceived);
  const onUrgentNotificationRef = useRef(onUrgentNotification);

  useEffect(() => {
    onNotificationReceivedRef.current = onNotificationReceived;
    onNotificationDeletedRef.current = onNotificationDeleted;
    onTaskReceivedRef.current = onTaskReceived;
    onUrgentNotificationRef.current = onUrgentNotification;
  });

  const socketRef = useRef<Socket | null>(null);
  const subscribedUserIdRef = useRef<string | undefined>(userId);
  const [state, setState] = useState<WebSocketState>({
    connected: false,
    connecting: false,
    error: null,
    lastMessage: null,
  });

  const connect = () => {
    if (socketRef.current?.connected) {
      return;
    }

    setState(prev => ({ ...prev, connecting: true, error: null }));

    try {
      const socket = io(`${getSocketBaseUrl()}/notifications`, {
        transports: ['websocket'],
        forceNew: true,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        setState(prev => ({ ...prev, connected: true, connecting: false, error: null }));

        if (userId) {
          subscribedUserIdRef.current = userId;
          socket.emit('subscribe', { userId, modules });
        }
      });

      socket.on('disconnect', () => {
        setState(prev => ({ ...prev, connected: false, connecting: false }));
      });

      socket.on('connect_error', (error) => {
        setState(prev => ({
          ...prev,
          connected: false,
          connecting: false,
          error: error.message,
        }));
      });

      socket.on('notification:new', (data: { data: Notification; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onNotificationReceivedRef.current?.(data.data);
      });

      socket.on('notification:updated', (data: { data: Notification; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onNotificationReceivedRef.current?.(data.data);
      });

      socket.on('notification:deleted', (data: { data: { id: string }; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onNotificationDeletedRef.current?.(data.data.id);
      });

      socket.on('task:new', (data: { data: Task; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onTaskReceivedRef.current?.(data.data);
      });

      socket.on('task:updated', (data: { data: Task; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onTaskReceivedRef.current?.(data.data);
      });

      socket.on('task:completed', (data: { data: Task; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onTaskReceivedRef.current?.(data.data);
      });

      socket.on('task:overdue', (data: { data: Task; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onTaskReceivedRef.current?.(data.data);
      });

      socket.on('notification:urgent', (data: { data: Notification; timestamp: string }) => {
        setState(prev => ({ ...prev, lastMessage: data }));
        onUrgentNotificationRef.current?.(data.data);
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'WebSocket connection failed';
      setState(prev => ({ ...prev, connecting: false, error: message }));
    }
  };

  const disconnect = () => {
    if (socketRef.current) {
      const subscribedUserId = subscribedUserIdRef.current;
      if (subscribedUserId) {
        socketRef.current.emit('unsubscribe', { userId: subscribedUserId });
      }
      socketRef.current.disconnect();
      socketRef.current = null;
      setState(prev => ({ ...prev, connected: false, connecting: false }));
    }
  };

  const subscribe = (newUserId: string, newModules: string[] = []) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe', { userId: newUserId, modules: newModules });
    }
  };

  const unsubscribe = (targetUserId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe', { userId: targetUserId });
    }
  };

  const ping = () => {
    socketRef.current?.connected && socketRef.current.emit('ping');
  };

  useEffect(() => {
    if (autoConnect && userId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, userId]);

  useEffect(() => {
    if (userId && socketRef.current?.connected) {
      subscribe(userId, modules);
    }
  }, [userId, modules.join(',')]);

  return {
    connected: state.connected,
    connecting: state.connecting,
    error: state.error,
    lastMessage: state.lastMessage,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    ping,
    socket: socketRef.current,
  };
}
